import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { CalendarEvent } from '@/lib/api/dashboard';
import type { DayForecast } from './WeatherStrip';
import { getAgenda, agendaByDay, AGENDA_META, type AgendaItem } from '@/lib/api/agenda';

interface MiniCalendarProps {
  events: CalendarEvent[];
  /** 14-day forecast (CR #98) — risky work days get a weather tint + icon */
  forecast?: DayForecast[];
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];
const DOW = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const toKey = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const typeColor: Record<string, string> = {
  'schedule': 'bg-primary',
  'job-start': 'bg-success',
  'job-end': 'bg-warning',
  // Agenda categories (CR #206)
  'money': 'bg-critical',
  'task': 'bg-blue-600',
  'other': 'bg-gray-400',
};

// Collapse agenda types into three dot categories so the tiny grid stays readable.
const agendaCat = (t: AgendaItem['type']): 'money' | 'task' | 'other' | null => {
  if (t === 'invoice_due' || t === 'payable') return 'money';
  if (t === 'task') return 'task';
  if (t === 'schedule' || t === 'job_start' || t === 'job_end') return null; // already dotted via events
  return 'other';
};

export const MiniCalendar = ({ events, forecast }: MiniCalendarProps) => {
  const today = new Date();
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const navigate = useNavigate();

  const forecastByDay = useMemo(() => {
    const map = new Map<string, DayForecast>();
    for (const f of forecast ?? []) map.set(f.date, f);
    return map;
  }, [forecast]);

  const year = cursor.getFullYear();
  const month = cursor.getMonth();

  // ── Agenda (CR #206): dated records for the visible month + a 7-day list ──
  const [monthAgenda, setMonthAgenda] = useState<AgendaItem[]>([]);
  const [upcoming, setUpcoming] = useState<AgendaItem[]>([]);

  useEffect(() => {
    const first = `${year}-${String(month + 1).padStart(2, '0')}-01`;
    const last = `${year}-${String(month + 1).padStart(2, '0')}-${String(new Date(year, month + 1, 0).getDate()).padStart(2, '0')}`;
    getAgenda(first, last).then(setMonthAgenda).catch(() => setMonthAgenda([]));
  }, [year, month]);

  useEffect(() => {
    const now = new Date();
    const in7 = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 7);
    getAgenda(toKey(now), toKey(in7))
      .then(items => setUpcoming(items.filter(i => i.type !== 'schedule')))
      .catch(() => setUpcoming([]));
  }, []);

  const agendaCatsByDay = useMemo(() => {
    const map = new Map<string, Set<string>>();
    for (const [day, items] of agendaByDay(monthAgenda)) {
      for (const it of items) {
        const cat = agendaCat(it.type);
        if (!cat) continue;
        if (!map.has(day)) map.set(day, new Set());
        map.get(day)!.add(cat);
      }
    }
    return map;
  }, [monthAgenda]);

  const eventsByDay = useMemo(() => {
    const map = new Map<string, Set<string>>();
    for (const ev of events) {
      if (!ev.date) continue;
      const key = ev.date.slice(0, 10);
      if (!map.has(key)) map.set(key, new Set());
      map.get(key)!.add(ev.type);
    }
    return map;
  }, [events]);

  const firstDow = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const todayKey = toKey(today);

  const cells: (number | null)[] = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const goToDay = (day: number) => {
    const mm = String(month + 1).padStart(2, '0');
    const dd = String(day).padStart(2, '0');
    const yy = String(year).slice(-2);
    navigate(`/calendar/day/${mm}-${dd}-${yy}`);
  };

  const upcomingLabel = (dateStr: string) =>
    new Date(dateStr + 'T12:00:00').toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric' });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold leading-tight tracking-[-0.015em] text-gray-900">Schedule</h2>
        <button onClick={() => navigate('/calendar')} className="text-sm font-bold text-primary hover:underline">
          Open calendar
        </button>
      </div>
      <div className="rounded-xl bg-white border border-gray-200 p-4">
        <div className="flex items-center justify-between pb-3">
          <button
            onClick={() => setCursor(new Date(year, month - 1, 1))}
            className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-primary/10"
            aria-label="Previous month"
          >
            <span className="material-symbols-outlined text-lg text-gray-600">chevron_left</span>
          </button>
          <p className="font-bold text-gray-900">{MONTHS[month]} {year}</p>
          <button
            onClick={() => setCursor(new Date(year, month + 1, 1))}
            className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-primary/10"
            aria-label="Next month"
          >
            <span className="material-symbols-outlined text-lg text-gray-600">chevron_right</span>
          </button>
        </div>
        <div className="grid grid-cols-7 gap-1 text-center text-sm">
          {DOW.map((d, i) => (
            <div key={i} className="font-medium text-gray-400 p-1 text-xs">{d}</div>
          ))}
          {cells.map((day, i) => {
            if (day === null) return <div key={i} />;
            const key = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const isToday = key === todayKey;
            const types = new Set<string>([
              ...(eventsByDay.get(key) ?? []),
              ...(agendaCatsByDay.get(key) ?? []),
            ]);
            const wx = forecastByDay.get(key);
            return (
              <button
                key={i}
                onClick={() => goToDay(day)}
                title={wx ? `${wx.t_max}°/${wx.t_min}° · ${wx.precip_prob}% precip${wx.risk !== 'none' ? ` — ${wx.risk === 'high' ? 'poor' : 'marginal'} work day` : ''}` : undefined}
                className={`relative flex flex-col items-center justify-center rounded-lg p-1.5 aspect-square transition-colors ${
                  isToday ? 'bg-primary text-white font-bold'
                  : wx?.risk === 'high' ? 'bg-critical/10 text-gray-900 hover:bg-critical/20'
                  : wx?.risk === 'caution' ? 'bg-warning/10 text-gray-900 hover:bg-warning/20'
                  : 'text-gray-900 hover:bg-primary/10'
                }`}
              >
                <span>{day}</span>
                {wx && wx.risk !== 'none' && !isToday && (
                  <span className={`material-symbols-outlined absolute top-0 right-0.5 text-[10px] leading-none ${wx.risk === 'high' ? 'text-critical' : 'text-warning'}`}>
                    {wx.snow_cm > 0 ? 'weather_snowy' : 'rainy'}
                  </span>
                )}
                {types.size > 0 && (
                  <span className="absolute bottom-1 flex gap-0.5">
                    {[...types].slice(0, 4).map((t, ti) => (
                      <span key={ti} className={`h-1 w-1 rounded-full ${isToday ? 'bg-white' : typeColor[t] || 'bg-gray-400'}`} />
                    ))}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-gray-100 pt-3 text-xs text-gray-500">
          <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-success" />Job start</span>
          <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-primary" />Scheduled</span>
          <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-warning" />Job end</span>
          <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-critical" />Money due</span>
          <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-blue-600" />Task</span>
          <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-gray-400" />Other dated</span>
          {forecast && forecast.length > 0 && (
            <span className="flex items-center gap-1"><span className="h-2 w-2 rounded bg-warning/30" />Weather risk</span>
          )}
        </div>
        {/* Coming up — next 7 days of dated records (CR #206) */}
        {upcoming.length > 0 && (
          <div className="mt-3 border-t border-gray-100 pt-3">
            <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wider text-gray-400">Coming up · next 7 days</p>
            <div className="flex flex-col gap-0.5">
              {upcoming.slice(0, 6).map((it, i) => {
                const meta = AGENDA_META[it.type];
                return (
                  <button
                    key={i}
                    onClick={() => navigate(it.link)}
                    className="flex items-center gap-2 rounded-lg px-1.5 py-1 text-left hover:bg-primary/10 transition-colors"
                    title={meta.name}
                  >
                    <span className={`material-symbols-outlined text-sm ${meta.chip.split(' ')[1]}`}>{meta.icon}</span>
                    <span className="min-w-0 flex-1 truncate text-xs text-gray-800">{it.label}</span>
                    <span className="shrink-0 text-[11px] font-medium text-gray-400">{upcomingLabel(it.date)}</span>
                  </button>
                );
              })}
              {upcoming.length > 6 && (
                <button
                  onClick={() => navigate('/calendar')}
                  className="px-1.5 py-0.5 text-left text-[11px] font-medium text-primary hover:underline"
                >
                  +{upcoming.length - 6} more on the calendar
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
